// src/components/vip/UpgradeModal.tsx
// Reusable VIP upgrade modal — rendered over skeleton for locked pages/features

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export type VipFeature = 'shopping_list' | 'recipes' | 'pdf_export' | 'modify_plan';

interface FeatureConfig {
  icon: string;
  titleKey: string;
  descriptionKey: string;
  bulletKeys: string[];
  accentColor: string;
}

const FEATURE_CONFIG: Record<VipFeature, FeatureConfig> = {
  shopping_list: {
    icon: 'ri-shopping-cart-2-line',
    titleKey: 'vip.upgradeModal.shoppingList.title',
    descriptionKey: 'vip.upgradeModal.shoppingList.description',
    bulletKeys: [
      'vip.upgradeModal.shoppingList.bullet1',
      'vip.upgradeModal.shoppingList.bullet2',
      'vip.upgradeModal.shoppingList.bullet3',
    ],
    accentColor: 'from-yellow-400 to-yellow-600',
  },
  recipes: {
    icon: 'ri-book-open-line',
    titleKey: 'vip.upgradeModal.recipes.title',
    descriptionKey: 'vip.upgradeModal.recipes.description',
    bulletKeys: [
      'vip.upgradeModal.recipes.bullet1',
      'vip.upgradeModal.recipes.bullet2',
      'vip.upgradeModal.recipes.bullet3',
    ],
    accentColor: 'from-yellow-400 to-orange-500',
  },
  pdf_export: {
    icon: 'ri-file-pdf-line',
    titleKey: 'vip.upgradeModal.pdfExport.title',
    descriptionKey: 'vip.upgradeModal.pdfExport.description',
    bulletKeys: [
      'vip.upgradeModal.pdfExport.bullet1',
      'vip.upgradeModal.pdfExport.bullet2',
      'vip.upgradeModal.pdfExport.bullet3',
    ],
    accentColor: 'from-yellow-400 to-yellow-600',
  },
  modify_plan: {
    icon: 'ri-edit-2-line',
    titleKey: 'vip.upgradeModal.modifyPlan.title',
    descriptionKey: 'vip.upgradeModal.modifyPlan.description',
    bulletKeys: [
      'vip.upgradeModal.modifyPlan.bullet1',
      'vip.upgradeModal.modifyPlan.bullet2',
      'vip.upgradeModal.modifyPlan.bullet3',
    ],
    accentColor: 'from-yellow-400 to-yellow-600',
  },
};

interface UpgradeModalProps {
  feature: VipFeature;
  // Optional — when provided, a close button is shown and clicking the backdrop dismisses the modal.
  // Omit for full-page skeleton gates where the user has no underlying content to return to.
  onClose?: () => void;
}

export default function UpgradeModal({ feature, onClose }: UpgradeModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = FEATURE_CONFIG[feature];

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-30 flex items-center justify-center p-4"
      onClick={onClose ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}
    >
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-full max-w-md border-2 border-yellow-400/60 shadow-2xl relative overflow-hidden animate-scale-in">

        {/* Ambient glow background */}
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-yellow-400 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-0 right-0 w-48 h-48 bg-orange-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>

        <div className="relative z-10 p-8">
          {/* Close button — only when modal is dismissible */}
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-gray-700/60 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          )}

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`w-20 h-20 bg-gradient-to-br ${config.accentColor} rounded-2xl flex items-center justify-center shadow-lg shadow-yellow-500/20`}>
              <i className={`${config.icon} text-black text-4xl`} />
            </div>
          </div>

          {/* VIP badge */}
          <div className="flex justify-center mb-4">
            <span className="flex items-center gap-1.5 px-3 py-1 bg-yellow-400/10 border border-yellow-400/30 rounded-full text-yellow-400 text-xs font-semibold tracking-wider uppercase">
              <i className="ri-vip-crown-line text-sm" />
              VIP
            </span>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white text-center mb-3">
            {t(config.titleKey)}
          </h2>

          {/* Description */}
          <p className="text-gray-400 text-center text-sm leading-relaxed mb-6">
            {t(config.descriptionKey)}
          </p>

          {/* Bullet points */}
          <div className="space-y-3 mb-8">
            {config.bulletKeys.map((key) => (
              <div key={key} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-yellow-400 text-xs" />
                </div>
                <span className="text-gray-300 text-sm leading-relaxed">
                  {t(key)}
                </span>
              </div>
            ))}
          </div>

          {/* Price hint */}
          <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 mb-6 text-center">
            <span className="text-gray-400 text-sm">{t('vip.upgradeModal.priceFrom')} </span>
            <span className="text-white font-bold text-lg">CHF 29.90</span>
            <span className="text-gray-400 text-sm">{t('vip.upgradeModal.perMonth')}</span>
          </div>

          {/* CTA */}
          <button
            onClick={() => navigate('/subscribe')}
            className={`w-full py-4 bg-gradient-to-r ${config.accentColor} text-black rounded-xl font-bold text-base hover:brightness-110 transition cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-yellow-500/20`}
          >
            <i className="ri-vip-crown-fill text-lg" />
            {t('vip.upgradeModal.cta')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          from { transform: scale(0.85); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
        .animate-scale-in {
          animation: scale-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
}